import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  Pressable,
  StyleSheet,
  ScrollView,
  Dimensions,
  Animated,
  Platform,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { Image } from 'expo-image';
import { useFocusEffect } from '@react-navigation/native';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { useTheme } from '../context/ThemeContext';
import { useUserPreferences } from '../context/UserPreferencesContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import { getApiUrl } from '../utils/apiConfig';
import {
  setLoggedInStudentId,
  getLoggedInStudentId,
  setUserSnapshot,
  getUserSnapshot,
  clearLoggedInSession,
  isUserRecord,
  hasSuperUserAccess,
  normalizeStudentUserRecord,
  showGettingStartedFlag,
  focusReportingPositionFromUser,
} from '../utils/sessionUser';
import { canAccessBuilders, canAccessTao2 } from '../utils/builderAccess';
import { getTao2TileImageCandidates, getTao2TileImageUrl } from '../utils/tao2';
import { getTile3dNativeStyle, getTile3dWebStyle } from '../utils/mainPageTileStyles';
import ClassBookTileArt from '../components/ClassBookTileArt';
import FocusModeHomeWidgets from '../components/FocusModeHomeWidgets';

const { width } = Dimensions.get('window');
const TILE_WIDTH = (width - 60) / 2; // 2 columns with padding

// Local assets
const BREAKS_BANNER_IMAGE = require('../../assets/IMG_7071.PNG');
const COURSES_IMAGE = require('../../assets/ChatGPT Image Jan 1, 2026, 02_28_17 AM.png');
const SESSION_IMAGE = require('../../assets/ChatGPT Image Jan 1, 2026, 02_35_46 AM.png');
const ASSIGN_EXERCISE_IMAGE = require('../../assets/ChatGPT Image Jan 28, 2026, 12_32_42 AM.png');
const GOALS_IMAGE = require('../../assets/ChatGPT Image Mar 13, 2026, 08_37_09 PM.png');
const SYLLABUS_TILE_IMAGE = require('../../assets/Screenshot 2026-04-10 at 20.17.12.png');
const SEND_MESSAGE_IMAGE = require('../../assets/iconmonstr-paper-plane-7-96.png');
const NOTES_TILE_IMAGE = require('../../assets/iconmonstr-pencil-square-lined-120.png');

const menuItems = [
  {
    id: 0,
    title: 'Getting Started',
    materialIcon: 'edit',
    navigation: 'GettingStarted',
    requiresGettingStartedVisible: true,
  },
  { id: 1, title: 'Create a new Session', icon: null, imageLocal: SESSION_IMAGE },
  { id: 2, title: 'My Courses', icon: null, imageLocal: COURSES_IMAGE, navigation: 'MyCourses' },
  { id: 3, title: 'My Breaks', icon: null, imageLocal: BREAKS_BANNER_IMAGE, navigation: 'MyBreaks' },
  { id: 31, title: 'Syllabus', icon: null, imageLocal: SYLLABUS_TILE_IMAGE, navigation: 'Syllabus' },
  { id: 4, title: 'Upload new breaks', icon: '⬆️', navigation: 'UploadNewBreaks' },
  { id: 5, title: 'Goals', icon: null, imageLocal: GOALS_IMAGE, navigation: 'Goals' },
  {
    id: 12,
    title: 'Send message',
    icon: null,
    imageLocal: SEND_MESSAGE_IMAGE,
    navigation: 'SendNotifications',
  },
  {
    id: 6,
    title: 'What should I train?',
    icon: '🔍',
    subtitle: 'Analytics/AI',
    navigation: 'WhatShouldITrain',
  },
  { id: 7, title: 'Builders', icon: '📋', navigation: 'BuilderMain' },
  { id: 8, title: 'Records', icon: '📊', navigation: 'Records', requiresSuperUser: true },
  { id: 9, title: 'Assign Exercise', icon: null, imageLocal: ASSIGN_EXERCISE_IMAGE, navigation: 'Assignments' },
  { id: 10, title: 'Schema Model Overview', icon: '🗂️', navigation: 'SchemaBuilder', requiresSuperUser: true },
  { id: 11, title: 'User Audit', icon: '🔍', navigation: 'UserAudit', requiresSuperUser: true },
  {
    id: 14,
    title: 'Adoption',
    icon: '🌟',
    subtitle: 'Stand out · Content gaps',
    navigation: 'Adoption',
    requiresSuperUser: true,
  },
  {
    id: 13,
    title: 'Notes',
    icon: null,
    imageLocal: NOTES_TILE_IMAGE,
    navigation: 'StudentNotes',
  },
  {
    id: 15,
    title: 'Class Book',
    customTile: 'classBook',
    navigation: 'ClassBook',
  },
  {
    id: 16,
    title: 'Tao 2',
    icon: '📖',
    taoTile: true,
    navigation: 'Tao2',
    requiresSecondDegree: true,
  },
];

function isHomeTileVisible(item, user, userFreshLoaded) {
  if (item.requiresSuperUser && !hasSuperUserAccess(user)) return false;
  if (item.requiresBlackBelt) {
    if (!userFreshLoaded || !user || !canAccessBuilders(user)) return false;
  }
  if (item.requiresSecondDegree) {
    if (!userFreshLoaded || !user || !canAccessTao2(user)) return false;
  }
  if (item.requiresGettingStartedVisible && !showGettingStartedFlag(user)) return false;
  return true;
}

export default function MainPage({ navigation, route }) {
  const theme = useTheme();
  const { setIsUSUserFromUser } = useUserPreferences();
  const scaleAnim = useRef({}).current;
  const [currentUser, setCurrentUser] = useState(null);
  const [userFreshLoaded, setUserFreshLoaded] = useState(false);
  const [taoTileUrl, setTaoTileUrl] = useState(getTao2TileImageUrl());
  const [taoTileCandidateIndex, setTaoTileCandidateIndex] = useState(0);
  const userRef = useRef(null); // Ref to track current user
  const stopwatchRef = useRef(null); // Ref for stopwatch component

  // Update ref when currentUser changes
  useEffect(() => {
    if (currentUser) {
      userRef.current = currentUser;
    }
  }, [currentUser]);

  // Restore session: route params, then stored student id + API, then last snapshot (offline / flaky API).
  useFocusEffect(
    React.useCallback(() => {
      let cancelled = false;

      const applyUser = async (u) => {
        if (cancelled || !isUserRecord(u)) return;
        const normalized = normalizeStudentUserRecord(u);
        await setUserSnapshot(normalized);
        setIsUSUserFromUser(normalized);
        setCurrentUser(normalized);
        userRef.current = normalized;
      };

      const load = async () => {
        setUserFreshLoaded(false);
        try {
          const paramId =
            route?.params?.userId != null
              ? Number(route.params.userId)
              : route?.params?.user?.id != null
                ? Number(route.params.user.id)
                : null;
          const storedId =
            paramId != null && !Number.isNaN(paramId)
              ? paramId
              : await getLoggedInStudentId();

          const optimisticUser = route?.params?.user ?? (await getUserSnapshot());
          if (optimisticUser && isUserRecord(optimisticUser) && !cancelled) {
            const normalized = normalizeStudentUserRecord(optimisticUser);
            setCurrentUser(normalized);
            userRef.current = normalized;
          }

          if (storedId != null && !Number.isNaN(storedId)) {
            await setLoggedInStudentId(storedId);
            const res = await fetch(`${getApiUrl()}/api/users/${storedId}`);
            let data = null;
            try {
              data = await res.json();
            } catch (_) {
              data = null;
            }
            if (res.ok && isUserRecord(data)) {
              await applyUser(data);
              if (!cancelled) setUserFreshLoaded(true);
              return;
            }
            if (res.status === 404) {
              await clearLoggedInSession();
            }
          }

          if (cancelled) return;

          if (userRef.current && isUserRecord(userRef.current)) {
            if (!cancelled) setUserFreshLoaded(true);
            return;
          }

          setCurrentUser(null);
          userRef.current = null;
          if (!cancelled) setUserFreshLoaded(true);

          const hasStoredSession = (await getLoggedInStudentId()) != null;
          if (!route?.params?.user && !hasStoredSession) {
            navigation.replace('Login');
          }
        } catch (e) {
          console.error('MainPage user load:', e);
          const snap = await getUserSnapshot();
          if (snap && !cancelled) {
            setCurrentUser(normalizeStudentUserRecord(snap));
            userRef.current = normalizeStudentUserRecord(snap);
          } else if (userRef.current && isUserRecord(userRef.current)) {
            setCurrentUser(userRef.current);
          }
          if (!cancelled) setUserFreshLoaded(true);
        }
      };

      load();
      return () => {
        cancelled = true;
      };
    }, [route?.params?.user, route?.params?.userId, navigation, setIsUSUserFromUser])
  );

  // Initialize scale animations for each tile
  menuItems.forEach(item => {
    if (!scaleAnim[item.id]) {
      scaleAnim[item.id] = new Animated.Value(1);
    }
  });

  const handleTilePress = (item) => {
    // Check super_user requirement
    const uTap = currentUser || userRef.current;
    if (item.requiresSuperUser && !hasSuperUserAccess(uTap)) {
      return;
    }
    if (item.requiresBlackBelt && (!userFreshLoaded || !uTap || !canAccessBuilders(uTap))) {
      return;
    }
    if (item.requiresSecondDegree && (!userFreshLoaded || !uTap || !canAccessTao2(uTap))) {
      return;
    }

    // Press feedback: native uses scale (web uses CSS translateY + shadows on Pressable)
    if (Platform.OS !== 'web') {
      Animated.sequence([
        Animated.timing(scaleAnim[item.id], {
          toValue: 0.97,
          duration: 90,
          useNativeDriver: true,
        }),
        Animated.timing(scaleAnim[item.id], {
          toValue: 1,
          duration: 90,
          useNativeDriver: true,
        }),
      ]).start();
    }

    // Prefer ref when React state hasn't committed yet (avoids null user on fast tap after login)
    const navUser = currentUser || userRef.current;
    const navUserId = navUser?.id || 1;

    // Navigate to appropriate screen based on item
    if (item.title === 'Create a new Session') {
      navigation.navigate('SessionBuilder', { userId: navUserId, user: navUser });
    } else if (item.navigation) {
      navigation.navigate(item.navigation, { userId: navUserId, user: navUser });
    } else {
      console.log(`Pressed: ${item.title}`);
    }
  };


  const styles = getStyles(theme);
  const homeUser = currentUser || userRef.current;
  const focusWidgetsTop = focusReportingPositionFromUser(homeUser) === 'top';

  return (
    <View
      style={styles.container}
      {...(Platform.OS === 'web'
        ? { dataSet: { screen: 'main', theme: theme.isDarkMode ? 'dark' : 'light' } }
        : {})}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
      
      {/* Header with Breadcrumb, Hamburger on Left, and Profile Icon on Right */}
      <View
        style={styles.header}
        {...(Platform.OS === 'web' ? { dataSet: { header: 'true' } } : { 'data-header': 'true' })}
      >
        <View
          style={styles.hamburgerContainer}
          {...(Platform.OS === 'web'
            ? {
                dataSet: {
                  lightMode: !theme.isDarkMode ? 'true' : 'false',
                  headerElement: 'hamburger',
                },
              }
            : {
                'data-light-mode': !theme.isDarkMode,
                'data-header-element': 'hamburger',
              })}
        >
          <HamburgerMenu
            navigation={navigation}
            stopwatchRef={stopwatchRef}
            currentUser={currentUser || userRef.current}
          />
        </View>
        <View
          style={styles.breadcrumbContainer}
          {...(Platform.OS === 'web'
            ? {
                dataSet: {
                  lightMode: !theme.isDarkMode ? 'true' : 'false',
                  headerElement: 'breadcrumb',
                },
              }
            : {
                'data-light-mode': !theme.isDarkMode,
                'data-header-element': 'breadcrumb',
              })}
        >
          <Text style={styles.breadcrumb}>Home</Text>
        </View>
        <View
          style={styles.profileIconContainer}
          {...(Platform.OS === 'web'
            ? {
                dataSet: {
                  lightMode: !theme.isDarkMode ? 'true' : 'false',
                  headerElement: 'profile',
                },
              }
            : {
                'data-light-mode': !theme.isDarkMode,
                'data-header-element': 'profile',
              })}
        >
          <ProfileIcon navigation={navigation} user={currentUser || userRef.current} />
        </View>
      </View>

      {/* Main Content — scroll shell fills flex area so light mode gutters aren’t transparent (RN Web global black) */}
      <View
        style={styles.scrollShell}
        nativeID="main-scroll-shell"
        {...(Platform.OS === 'web'
          ? { dataSet: { mainScrollShell: 'true' } }
          : {})}
      >
        <ScrollView
          style={styles.content}
          contentContainerStyle={styles.contentContainer}
          showsVerticalScrollIndicator={false}
        >
          {focusWidgetsTop ? (
            <FocusModeHomeWidgets user={homeUser} theme={theme} />
          ) : null}
          <View
            style={styles.tilesContainer}
            nativeID="main-tiles-container"
            {...(Platform.OS === 'web'
              ? { dataSet: { mainTilesContainer: 'true' } }
              : {})}
          >
            {menuItems
            .filter(item => isHomeTileVisible(item, currentUser || userRef.current, userFreshLoaded))
            .map((item, index) => {
              // Adjust index for filtered items
              const u = currentUser || userRef.current;
              const filteredItems = menuItems.filter((i) =>
                isHomeTileVisible(i, u, userFreshLoaded)
              );
              const adjustedIndex = filteredItems.indexOf(item);
              const TileOuter = Platform.OS === 'web' ? View : Animated.View;
              const outerTransform =
                Platform.OS === 'web'
                  ? []
                  : [{ scale: scaleAnim[item.id] }];
              const activeUser = currentUser || userRef.current;
              const blocked = !isHomeTileVisible(item, activeUser, userFreshLoaded);

              return (
                <TileOuter
                  key={item.id}
                  style={[
                    Platform.OS !== 'web' && { transform: outerTransform },
                    adjustedIndex % 2 === 0 && styles.tileLeft,
                    adjustedIndex % 2 === 1 && styles.tileRight,
                  ]}
                  {...(Platform.OS === 'web' ? { dataSet: { mainMenuTileWrap: 'true' } } : {})}
                >
                  <Pressable
                    disabled={blocked}
                    onPress={() => handleTilePress(item)}
                    {...(Platform.OS === 'web' ? { dataSet: { mainMenuTile: 'true' } } : {})}
                    style={({ pressed }) => [
                      styles.tile,
                      Platform.OS === 'web' && getTile3dWebStyle(theme, pressed && !blocked),
                      Platform.OS !== 'web' &&
                        getTile3dNativeStyle(theme, pressed && !blocked),
                      blocked && styles.tileDisabled,
                    ]}
                  >
                    {item.materialIcon ? (
                      <MaterialIcons
                        name={item.materialIcon}
                        size={44}
                        color={theme.colors.heading || '#d4af37'}
                        style={styles.tileMaterialIcon}
                      />
                    ) : item.customTile === 'classBook' ? (
                      <ClassBookTileArt style={styles.tileImage} />
                    ) : item.taoTile && taoTileUrl ? (
                      <Image
                        source={{ uri: taoTileUrl }}
                        style={styles.tileImage}
                        contentFit="contain"
                        onError={() => {
                          const candidates = getTao2TileImageCandidates();
                          const next = taoTileCandidateIndex + 1;
                          if (next < candidates.length) {
                            setTaoTileCandidateIndex(next);
                            setTaoTileUrl(candidates[next]);
                          } else {
                            setTaoTileUrl('');
                          }
                        }}
                      />
                    ) : item.imageLocal ? (
                      <Image
                        source={item.imageLocal}
                        style={styles.tileImage}
                        contentFit="contain"
                      />
                    ) : item.imageUrl ? (
                      <Image
                        source={{ uri: item.imageUrl }}
                        style={styles.tileImage}
                        contentFit="contain"
                      />
                    ) : (
                      <Text style={styles.tileIcon}>{item.icon}</Text>
                    )}
                    <Text style={styles.tileTitle}>{item.title}</Text>
                    {item.subtitle && (
                      <Text style={styles.tileSubtitle}>{item.subtitle}</Text>
                    )}
                  </Pressable>
                </TileOuter>
              );
            })}
          </View>
          {!focusWidgetsTop ? (
            <FocusModeHomeWidgets user={homeUser} theme={theme} />
          ) : null}
        </ScrollView>
      </View>
      
      {/* Stopwatch Component */}
      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

const getStyles = (theme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 50,
    paddingBottom: 20,
    paddingHorizontal: 20,
    backgroundColor: theme.isDarkMode ? '#000000' : '#FFFFFF',
    borderBottomWidth: 0,
    ...(Platform.OS === 'web' && !theme.isDarkMode
      ? { boxShadow: '0 1px 0 rgba(0,0,0,0.06)' }
      : {}),
  },
  hamburgerContainer: {
    marginRight: 15,
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  breadcrumbContainer: {
    flex: 1,
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  breadcrumb: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.heading,
    letterSpacing: 0.5,
    /* RN Web: opaque bg emits r-backgroundColor → skips global black strip under title (match Session Builder) */
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  profileIconContainer: {
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  scrollShell: {
    flex: 1,
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
  },
  content: {
    flex: 1,
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
  },
  contentContainer: {
    padding: 20,
    paddingBottom: 40,
    flexGrow: 1,
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
  },
  tilesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    flexGrow: 1,
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
  },
  tile: {
    width: TILE_WIDTH,
    height: 180,
    borderRadius: 14,
    padding: 20,
    marginBottom: 24,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: Platform.OS === 'web' ? 'visible' : 'hidden',
    ...(Platform.OS === 'web'
      ? {
          backgroundColor: 'transparent',
        }
      : {
          backgroundColor: theme.isDarkMode ? theme.colors.surface : '#faf8f4',
          borderWidth: 2,
          borderColor: 'rgba(212, 175, 55, 0.85)',
        }),
  },
  tileDisabled: {
    opacity: 0.45,
  },
  tileLeft: {
    marginRight: 10,
    alignSelf: 'flex-start',
  },
  tileRight: {
    marginLeft: 10,
    alignSelf: 'flex-start',
  },
  tileIcon: {
    fontSize: 48,
    marginBottom: 12,
    backgroundColor: 'transparent',
  },
  tileMaterialIcon: {
    marginBottom: 12,
    backgroundColor: 'transparent',
  },
  tileImage: {
    width: '100%',
    height: 80,
    marginBottom: 12,
  },
  tileTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.isDarkMode ? theme.colors.text : '#000000',
    textAlign: 'center',
    marginBottom: 4,
    backgroundColor: 'transparent',
  },
  tileSubtitle: {
    fontSize: 12,
    color: theme.isDarkMode ? theme.colors.primary : '#b8942f',
    textAlign: 'center',
    marginTop: 4,
    backgroundColor: 'transparent',
  },
});
